import { PetCareExperienceOptions } from './PetCareProfileTypes'; // Ensure PetCareExperience is an enum or object
export interface SelectOption {
  value: string;
  display: string;
}

export interface CheckboxOption {
  value: string;
  label: string;
}

export const EDUCATION_LEVELS = [
  'Some High School',
  'High School Degree',
  'Some College',
  'College Degree',
  'Some Graduate School',
  'Graduate Degree',
];

export interface EducationFormState {
  levelOfEducation: string;
  schoolName: string;
  currentlyAttending: boolean;
  major: string;
  yearGraduated: number | null;
}

export const educationLevelOptions = (): SelectOption[] =>
  EDUCATION_LEVELS.map((level) => ({
    value: level.replace(/\s+/g, '_').toUpperCase(),
    display: level,
  }));

export const graduationYearOptions = (): SelectOption[] => {
  const currentYear = new Date().getFullYear();
  return Array.from({ length: 60 }, (_, index) => {
    const year = String(currentYear - index);
    return { value: year, display: year };
  });
};

export const distanceOptions = (): SelectOption[] =>
  [1, 2, 5, 10, 15, 25, 40, 50].map((miles) => ({
    value: String(miles),
    display: `${miles} miles`,
  }));

// ---------------------------------------------------------------------------
// Detailed availability windows shared by Companion Care + Housekeeping detailed
// schedules.
// ---------------------------------------------------------------------------
export const DETAILED_AVAILABILITY_OPTIONS: CheckboxOption[] = [
  { value: 'WEEKDAY_MORNINGS', label: 'Weekday Early Morning' },
  { value: 'WEEKDAY_DAYS', label: 'Day-time Weekdays (M - F)' },
  { value: 'WEEKDAY_AFTERNOONS', label: 'Weekday Afternoons' },
  { value: 'WEEKDAY_EVENINGS', label: 'Weekday Evenings' },
  { value: 'WEEKEND_DAYS', label: 'Weekends Daytime' },
  { value: 'WEEKEND_EVENINGS', label: 'Weekends Evenings' },
];

export const TYPICAL_AVAILABILITY_OPTIONS: CheckboxOption[] = [
  { value: 'OPEN', label: 'Open (all day, everyday)' },
  { value: 'WEEKDAYS', label: 'Open Availability on Weekdays (M-F)' },
  { value: 'OTHER', label: 'Other' },
];

// ---------------------------------------------------------------------------
// Housekeeping
// ---------------------------------------------------------------------------

export const HOUSEKEEPING_TYPES_OF_SERVICES_OPTIONS: CheckboxOption[] = [
  { value: 'CLEANING', label: 'Cleaning' },
  { value: 'ERRANDS', label: 'Errands' },
  { value: 'MAINTENANCE', label: 'Maintenance' },
];

export const HOUSEKEEPING_SERVICES_EXPERIENCE_OPTIONS: CheckboxOption[] = [
  { value: 'GENERAL_CLEANING', label: 'General Cleaning' },
  { value: 'SPECIALIZED_CLEANING', label: 'Specialized Cleaning' },
  { value: 'PREPARE_SNACKS_MEALS', label: 'Prepare Snacks/Meals' },
  { value: 'WASH_DISHES', label: 'Wash Dishes' },
  { value: 'DO_LAUNDRY', label: 'Do Laundry' },
  { value: 'RUN_ERRANDS', label: 'Run Errands' },
  { value: 'GENERAL_MAINTENANCE', label: 'General Maintenance' },
  { value: 'ORGANIZATION', label: 'Organization' },
];

// ---------------------------------------------------------------------------
// Pet care
// ---------------------------------------------------------------------------
export const PET_CARE_SERVICES_EXPERIENCE_OPTIONS: any = Object.values(PetCareExperienceOptions).map((experience) => ({
  value: experience,
  label: experience
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/^\w/, (c) => c.toUpperCase()),
}));

// ---------------------------------------------------------------------------
// Companion Care
// ---------------------------------------------------------------------------

export const COMPANION_CARE_AGE_GROUP_OPTIONS: CheckboxOption[] = [
  { value: 'UNDER_55', label: 'Under 55 years old' },
  { value: 'FROM_55_TO_64', label: '55 to 64 years old' },
  { value: 'FROM_65_TO_74', label: '65 to 74 years old' },
  { value: 'FROM_75_TO_84', label: '75 to 84 years old' },
  { value: 'OVER_85', label: 'Over 85 years old' },
];

export const COMPANION_CARE_BASIC_EXPERIENCE_OPTIONS: CheckboxOption[] = [
  { value: 'CONVERSATION_COMPANIONSHIP', label: 'Conversation and companionship' },
  { value: 'SAFETY_SUPERVISION', label: 'Safety supervision' },
  { value: 'MEAL_PREPARATION', label: 'Meal preparation' },
  { value: 'MEDICATION_REMINDERS', label: 'Medication reminders' },
  { value: 'SOCIAL_ACTIVITIES', label: 'Social activities' },
  { value: 'LIGHT_HOUSEKEEPING', label: 'Light housekeeping' },
  { value: 'EXERCISE_PHYSICAL_ACTIVITIES', label: 'Exercise or physical activities' },
  { value: 'LAUNDRY_LINEN_CHANGES', label: 'Laundry and linen changes' },
  { value: 'SHOPPING', label: 'Shopping' },
  { value: 'MOBILITY_ASSISTANCE', label: 'Mobility assistance' },
];

export const COMPANION_CARE_PERSONAL_EXPERIENCE_OPTIONS: CheckboxOption[] = [
  { value: 'BATHING', label: 'Bathing' },
  { value: 'DRESSING', label: 'Dressing' },
  { value: 'GROOMING', label: 'Grooming' },
  { value: 'FEEDING_SPECIAL_DIET', label: 'Feeding and special diet' },
  { value: 'TOILETING', label: 'Toileting' },
];

export const CERTIFICATIONS_OPTIONS: CheckboxOption[] = [
  { value: 'FIRST_AID_TRAINING', label: 'First Aid' },
  { value: 'CPR_CERTIFIED', label: 'CPR' },
];

// ---------------------------------------------------------------------------
// Child care (nanny + babysitter)
// ---------------------------------------------------------------------------

export const CHILD_CARE_AGE_GROUP_OPTIONS: CheckboxOption[] = [
  { value: 'FIRST_YEAR', label: 'Infant (age 0-11 months)' },
  { value: 'TODDLERS', label: 'Toddler (age 1-3)' },
  { value: 'PRESCHOOLERS', label: 'Preschool (age 4-5)' },
  { value: 'GRADE_SCHOOL_AGE', label: 'Elementary (age 6-10)' },
  { value: 'PRE_TEEN_TEENAGERS', label: 'Pre-teens & teens (age 11+)' },
];

export const CHILD_CARE_ADDITIONAL_SERVICES_OPTIONS: CheckboxOption[] = [
  { value: 'MEAL_PREPARATION', label: 'Meal preparation' },
  { value: 'LAUNDRY', label: 'Laundry' },
  { value: 'LIGHT_HOUSEKEEPING', label: 'Light housekeeping' },
  { value: 'SWIMMING_SUPERVISION', label: 'Swimming supervision' },
  { value: 'CRAFTS', label: 'Crafts' },
  { value: 'POTTY_TRAINING', label: 'Toilet training' },
  { value: 'TRAVEL_WITH_FAMILY', label: 'Travel with family' },
  { value: 'SICK_CARE', label: 'Sick care' },
  { value: 'RUN_ERRANDS', label: 'Willing to run errands' },
  { value: 'DRIVE_CHILDREN', label: 'School pick up and drop off' },
  { value: 'TWINS', label: 'Twins experience' },
];

export const CHILD_CARE_SPECIALTY_QUALIFICATIONS_OPTIONS: CheckboxOption[] = [
  { value: 'FIRST_AID_TRAINING', label: 'First Aid Training' },
  { value: 'CPR_CERTIFIED', label: 'CPR Training' },
  { value: 'REGISTERED_NURSE', label: 'Registered Nurse' },
  { value: 'CERTIFIED_NURSING_ASSISTANT', label: 'Certified Nursing Assistant' },
  { value: 'LICENSED_PRACTICAL_NURSE', label: 'Licensed Practical Nurse' },
  { value: 'LICENSED_VOCATIONAL_NURSE', label: 'Licensed Vocational Nurse' },
  { value: 'OCCUPATIONAL_THERAPIST', label: 'Occupational Therapist' },
  { value: 'PHYSICAL_THERAPIST', label: 'Physical Therapist' },
  { value: 'SPEECH_THERAPIST', label: 'Speech Therapist' },
  { value: 'EARLY_CHILDHOOD_SPECIAL_EDUCATION_CERTIFICATION', label: 'Early childhood special education certification' },
  { value: 'SPECIAL_EDUCATION_CERTIFICATION', label: 'Special education certification' },
  { value: 'EXPERIENCE_DOULA', label: 'Experience as a doula' },
];

export const CHILD_CARE_MAX_CHILDREN_OPTIONS: SelectOption[] = [
  { value: '1', display: '1 child' },
  { value: '2', display: '2 children' },
  { value: '3', display: '3 children' },
  { value: '4', display: '4+ children' },
];

export const INFANT_CARE_EXPERIENCE_OPTIONS: SelectOption[] = [
  { value: 'ONE', display: '1 year' },
  { value: 'TWO_TO_THREE', display: '2 to 3 years' },
  { value: 'FOUR_TO_FIVE', display: '4 to 5 years' },
  { value: 'SIX_TO_TEN', display: '6 to 10 years' },
  { value: 'MORE_THAN_TEN', display: 'More than 10 years' },
];

export const INFANT_CARE_SERVICES_OPTIONS: CheckboxOption[] = [
  { value: 'INSTRUCTION', label: 'Instruction (proper swaddling, feeding, changing, bathing)' },
  { value: 'SETTING_ROUTINE', label: 'Assist with setting a routine' },
  { value: 'OCCASIONAL_CARE', label: 'Occasional care' },
  { value: 'MOTHERS_HELPER', label: "Mother's helper" },
  { value: 'OVERNIGHT_CARE', label: 'Overnight care' },
];

export const HOMEWORK_HELP_TYPE_OPTIONS: CheckboxOption[] = [
  { value: 'FINISHING_HOMEWORK', label: 'Finishing homework' },
  { value: 'CHECKING_ANSWERS', label: 'Checking answers' },
  { value: 'STUDYING_FOR_TEST', label: 'Studying for a test' },
  { value: 'PROOFREADING', label: 'Proofreading' },
];

export const HOMEWORK_HELP_SUBJECT_OPTIONS: CheckboxOption[] = [
  { value: 'BASIC_MATH', label: 'Basic math' },
  { value: 'PRE_ALGEBRA', label: 'Pre-algebra' },
  { value: 'ELEMENTARY_SCIENCE', label: 'Elementary science' },
  { value: 'EARTH_SCIENCE', label: 'Earth science' },
  { value: 'SCIENCE', label: 'Science' },
  { value: 'ENGLISH', label: 'English' },
  { value: 'ESSAY_WRITING', label: 'Essay writing' },
  { value: 'SOCIAL_STUDIES', label: 'Social studies' },
];

export const SPECIAL_NEEDS_YEARS_OPTIONS: SelectOption[] = [
  { value: '1', display: '1 year' },
  { value: '2-3', display: '2 to 3 years' },
  { value: '4-5', display: '4 to 5 years' },
  { value: '6-10', display: '6 to 10 years' },
  { value: '11+', display: 'More than 10 years' },
];

export const SPECIAL_NEEDS_AGE_GROUP_OPTIONS: CheckboxOption[] = [
  { value: 'INFANT', label: 'Infants' },
  { value: 'TODDLER', label: 'Toddlers' },
  { value: 'YOUTH', label: 'Youths' },
  { value: 'TEEN', label: 'Teens' },
  { value: 'ADULT', label: 'Adults' },
  { value: 'SENIOR', label: 'Seniors' },
];

export const SPECIAL_NEEDS_CARE_OPTIONS: CheckboxOption[] = [
  { value: 'COGNITIVE_DISABILITIES', label: 'Cognitive disabilities' },
  { value: 'PHYSICAL_DISABILITIES', label: 'Physical disabilities' },
  { value: 'MEDICAL_NEEDS', label: 'Medical/Special healthcare needs' },
  { value: 'SOCIAL_EMOTIONAL', label: 'Social/emotional' },
];

export const SPECIAL_NEEDS_CONDITION_OPTIONS: CheckboxOption[] = [
  { value: 'ALZHEIMER', label: "Alzheimer's" },
  { value: 'ASPERGERS_SYNDROME', label: "Asperger's Syndrome" },
  { value: 'ATTENTION_DEFICIT_AND_HYPERACTIVITY_DISORDER', label: 'Attention Deficit and Hyperactivity Disorder' },
  { value: 'ATTENTION_DEFICIT_DISORDER', label: 'Attention Deficit Disorder' },
  { value: 'AUTISM', label: 'Autism' },
  { value: 'AUTISM_SPECTRUM_DISORDER', label: 'Autism Spectrum Disorder' },
  { value: 'CANCER', label: 'Cancer' },
  { value: 'CENTRAL_AUDITORY_PROCESSING_DISORDER', label: 'Central Auditory Processing Disorder' },
  { value: 'CEREBRAL_PALSY', label: 'Cerebral Palsy' },
  { value: 'CYSTIC_FIBROSIS', label: 'Cystic Fibrosis' },
  { value: 'DELAYED_SPEECH', label: 'Delayed Speech' },
  { value: 'DEVELOPMENTAL_LANGUAGE_DISORDER', label: 'Developmental Language Disorder' },
  { value: 'DIABETES', label: 'Diabetes' },
  { value: 'DOWN_SYNDROME', label: 'Down Syndrome' },
  { value: 'DWARFISM', label: 'Dwarfism' },
  { value: 'DYSLEXIA', label: 'Dyslexia' },
  { value: 'EPILEPSY', label: 'Epilepsy' },
  { value: 'FETAL_ALCOHOL_SYNDROME', label: 'Fetal Alcohol Syndrome' },
  { value: 'FRAGILE_X_SYNDROME', label: 'Fragile X Syndrome' },
  { value: 'HEARING_DISORDERS', label: 'Hearing Disorders' },
  { value: 'HEART_DEFECTS', label: 'Heart Defects' },
  { value: 'HYDROCEPHALY', label: 'Hydrocephaly' },
  { value: 'JUVENILE_DIABETES', label: 'Juvenile Diabetes' },
  { value: 'JUVENILE_RHEUMATOID_ARTHRITIS', label: 'Juvenile Rheumatoid Arthritis' },
  { value: 'LEARNING_DISABILITIES', label: 'Learning Disabilities' },
  { value: 'MENTAL_ILLNESS', label: 'Mental Illness' },
  { value: 'MOBILITY_CHANGES', label: 'Mobility Changes' },
  { value: 'MULTIPLE_SCLEROSIS', label: 'Multiple Sclerosis' },
  { value: 'MUSCULAR_DYSTROPHY', label: 'Muscular Dystrophy' },
  { value: 'PERVASIVE_DEVELOPMENT_DISORDER', label: 'Pervasive Development Disorder' },
  { value: 'PREMATURE_NEWBORN', label: 'Premature Newborn' },
  { value: 'RETT_SYNDROME', label: 'Rett Syndrome' },
  { value: 'SEIZURES', label: 'Seizures' },
  { value: 'SENSORY_INTEGRATION_DISORDER', label: 'Sensory Integration Disorder' },
  { value: 'SPINAL_CORD_INJURY', label: 'Spinal Cord Injury' },
  { value: 'TOURETTE_SYNDROME', label: 'Tourette Syndrome' },
  { value: 'VISUAL_IMPAIRMENT', label: 'Visual Impairment' },
];

export const SPECIAL_NEEDS_SERVICE_OPTIONS: CheckboxOption[] = [
  { value: 'ADMINISTERING_MEDICATION', label: 'Administering Medication' },
  { value: 'APPLIED_BEHAVIOR_ANALYSIS', label: 'Applied Behavior Analysis' },
  { value: 'ASSISTANCE_WITH_TRANSPORTATION', label: 'Assistance With Transportation' },
  { value: 'BATHING', label: 'Bathing' },
  { value: 'BED_BATHS', label: 'Bed Baths' },
  { value: 'BLADDER_AND_BOWEL_ASSISTANCE', label: 'Bladder and Bowel Assistance' },
  { value: 'BLOOD_GLUCOSE_TESTING', label: 'Blood Glucose Testing' },
  { value: 'DRESSING_ASSISTANCE', label: 'Dressing Assistance' },
  { value: 'FEEDING_ASSISTANCE', label: 'Feeding Assistance' },
  { value: 'FEEDING_TUBE', label: 'Feeding Tube' },
  { value: 'GROOMING_AND_HYGIENE', label: 'Grooming and Hygiene' },
  { value: 'INJECTION_ASSISTANCE', label: 'Injection Assistance' },
  { value: 'INSULIN_INJECTIONS', label: 'Insulin Injections' },
  { value: 'LIFTING_AND_TRANSFERRING', label: 'Lifting and Transferring' },
  { value: 'MOBILITY_ASSISTANCE', label: 'Mobility Assistance' },
  { value: 'POSITIVE_BEHAVIORAL_SUPPORT', label: 'Positive Behavioral Support' },
  { value: 'RECREATIONAL_MUSIC_OR_ART_THERAPY', label: 'Recreational Music or Art Therapy' },
  { value: 'REPOSITIONING', label: 'Repositioning' },
  { value: 'RESPIRATORY_CARE', label: 'Respiratory Care' },
  { value: 'SIGN_LANGUAGE', label: 'Sign Language' },
  { value: 'SPECIFIC_MEAL_PREPARATION', label: 'Specific meal preparation' },
  { value: 'WILBARGER_BRUSHING_PROTOCOL', label: 'Wilbarger Brushing Protocol' },
];

// Full specialty-skills qualifications list (matches Rails professional skills)
export const COMPANION_CARE_SPECIALTY_QUALIFICATIONS_OPTIONS: CheckboxOption[] = [
  { value: 'FIRST_AID_TRAINING', label: 'First Aid Training' },
  { value: 'CPR_CERTIFIED', label: 'CPR Training' },
  { value: 'REGISTERED_NURSE', label: 'Registered Nurse' },
  { value: 'CERTIFIED_NURSING_ASSISTANT', label: 'Certified Nursing Assistant' },
  { value: 'LICENSED_PRACTICAL_NURSE', label: 'Licensed Practical Nurse' },
  { value: 'LICENSED_VOCATIONAL_NURSE', label: 'Licensed Vocational Nurse' },
  { value: 'OCCUPATIONAL_THERAPIST', label: 'Occupational Therapist' },
  { value: 'PHYSICAL_THERAPIST', label: 'Physical Therapist' },
  { value: 'SPEECH_THERAPIST', label: 'Speech Therapist' },
  { value: 'BONDED', label: 'Bonded' },
  { value: 'LICENSED', label: 'Licensed' },
  { value: 'INSURED', label: 'Insured' },
];

export const COMPANION_CARE_INDIVIDUALIZED_SERVICES_OPTIONS: CheckboxOption[] = [
  { value: 'MEDICATION_ADMINISTRATION', label: 'Medication administration' },
  { value: 'MEDICATION_ASSISTANCE', label: 'Medication assistance' },
  { value: 'MEDICATION_MANAGEMENT', label: 'Medication management' },
  { value: 'GROOMING_GUIDANCE', label: 'Grooming guidance' },
  { value: 'ASSISTANCE_WITH_PETS', label: 'Assistance with pets' },
  { value: 'MEAL_PLANNING', label: 'Meal planning' },
  { value: 'NUTRITION_EDUCATION', label: 'Nutrition education' },
  { value: 'MOBILITY_TRAINING', label: 'Mobility training' },
];

export const COMPANION_CARE_HEALTH_SERVICES_OPTIONS: CheckboxOption[] = [
  { value: 'WOUND_CARE', label: 'Wound care' },
  { value: 'PAIN_MANAGEMENT', label: 'Pain management' },
  { value: 'IV_THERAPY_INJECTIONS', label: 'IV therapy/injections' },
  { value: 'DIABETIC_CARE', label: 'Diabetic care' },
  { value: 'CENTRAL_LINE_CARE', label: 'Central line care' },
  { value: 'OSTOMY_CARE', label: 'Ostomy care' },
];

export interface CompanionCareServiceRateOption {
  service: string;
  checkboxLabel: string;
  inputLabel: string;
  ratePath:
    | 'homeCareRates.hourly'
    | 'homeCareRates.halfDay'
    | 'homeCareRates.fullDay'
    | 'homeCareRates.weekly'
    | 'homeCareRates.monthly'
    | 'respiteCareRates.hourly'
    | 'respiteCareRates.halfDay'
    | 'respiteCareRates.fullDay'
    | 'transportationRates.oneWay';
}

export const COMPANION_CARE_SERVICE_RATE_OPTIONS: CompanionCareServiceRateOption[] = [
  {
    service: 'HOME_CARE_HOUR',
    checkboxLabel: 'Home care- hourly rate',
    inputLabel: '$ Hourly rate',
    ratePath: 'homeCareRates.hourly',
  },
  {
    service: 'HOME_CARE_HALF_DAY',
    checkboxLabel: 'Home care- half-day rate',
    inputLabel: '$ Half day rate',
    ratePath: 'homeCareRates.halfDay',
  },
  {
    service: 'HOME_CARE_DAY',
    checkboxLabel: 'Home care- full-day rate',
    inputLabel: '$ Full day rate',
    ratePath: 'homeCareRates.fullDay',
  },
  {
    service: 'HOME_CARE_WEEK',
    checkboxLabel: 'Home care- weekly rate',
    inputLabel: '$ Weekly Rate',
    ratePath: 'homeCareRates.weekly',
  },
  {
    service: 'HOME_CARE_MONTH',
    checkboxLabel: 'Home care- monthly rate',
    inputLabel: '$ Monthly Rate',
    ratePath: 'homeCareRates.monthly',
  },
  {
    service: 'RESPITE_CARE_HOUR',
    checkboxLabel: 'Respite care- hourly rate',
    inputLabel: '$ Hourly Rate',
    ratePath: 'respiteCareRates.hourly',
  },
  {
    service: 'RESPITE_CARE_HALF_DAY',
    checkboxLabel: 'Respite care- half-day rate',
    inputLabel: '$ Half day rate',
    ratePath: 'respiteCareRates.halfDay',
  },
  {
    service: 'RESPITE_CARE_DAY',
    checkboxLabel: 'Respite care- full-day rate',
    inputLabel: '$ Full day rate',
    ratePath: 'respiteCareRates.fullDay',
  },
  {
    service: 'TRANSPORT_ONE_WAY',
    checkboxLabel: 'Transportation- one-way rate',
    inputLabel: '$ Rate (one way)',
    ratePath: 'transportationRates.oneWay',
  },
];

export const numberOfPetOptions = (): SelectOption[] => [
  { value: '1', display: 'One pet' },
  { value: '2', display: 'Two pets' },
  { value: '3', display: 'Three pets' },
  { value: '4', display: 'Four or more' },
];

// ---------------------------------------------------------------------------
// Child care
// ---------------------------------------------------------------------------
export const CHILD_CARE_GENERAL_SERVICE_OPTIONS: { key: string; label: string }[] = [
  { key: 'homeworkHelp', label: 'Homework help' },
  { key: 'willingToDriveChildren', label: 'Can drive kids' },
  { key: 'willingToRunErrands', label: 'Can run errands' },
  { key: 'comfortableWithPets', label: 'Works with pets' },
  { key: 'sickChildren', label: 'Sick care' },
  { key: 'infantCare', label: 'Infant care' },
  { key: 'specialNeeds', label: 'Special needs care' },
  { key: 'twins', label: 'Twins experience' },
  { key: 'accessToTransportation', label: 'Has a car' },
];

export const CHILD_CARE_ADDITIONAL_SERVICE_OPTIONS: CheckboxOption[] = [
  { value: 'MEAL_PREPARATION', label: 'Prepare snacks/meals' },
  { value: 'LAUNDRY', label: 'Laundry' },
  { value: 'LIGHT_HOUSEKEEPING', label: 'Housekeeping' },
  { value: 'SWIMMING_SUPERVISION', label: 'Swimming supervision' },
  { value: 'CRAFTS', label: 'Crafts' },
  { value: 'POTTY_TRAINING', label: 'Toilet training' },
  { value: 'TRAVEL_WITH_FAMILY', label: 'Travel with family' },
];

export const INFANT_CARE_SERVICE_OPTIONS: CheckboxOption[] = [
  { value: 'INSTRUCTION', label: 'Instruction (proper swaddling, feeding, changing, bathing)' },
  { value: 'SETTING_ROUTINE', label: 'Assist with setting a routine' },
  { value: 'OCCASIONAL_CARE', label: 'Occasional care' },
  { value: 'MOTHERS_HELPER', label: "Mother's helper" },
  { value: 'OVERNIGHT_CARE', label: 'Overnight care' },
];
